import { TableTemplate } from "components/templates/default/table/TableTemplate";
import * as s from "./InstallStyled";
import BigButton from "components/atoms/buttons/BigButton";
import { ReactComponent as EXPORT_ICON } from "assets/icons/export.svg";
import { Text, ValidateWord } from "components/atoms/text/Text";
import DefaultThumbnail from "components/atoms/image/DefaultThumbnail";
import { useMediaQuery } from "react-responsive";
import * as mediaQuery from "components/MediaQuery";
import { InstallationView } from "api/interfaces/reportInterface.interface";
import VideoThumbnail from "components/atoms/image/thumbnail/VideoThumbnail";
import { ClickSearch } from "components/atoms/input/search/SearchInput";
import { Spin } from "antd";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import TextArea from "components/atoms/input/TextArea";
import EmailSelectComponent from "components/atoms/select/email";
import { useAppSelector } from "redux/hooks";

import {
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useMenu } from "hook/useMenu";
import useApiError from "hook/useApiError";
import SmallPopupDefaultTemplate from "components/templates/modal/small/SmallPopupDefaultTemplate";
import { FaTools } from "react-icons/fa";
import TableButton from "components/atoms/buttons/TableButton";
import DetailHeader from "components/blocks/subheader/DetailHeader";
import CovertThumbnail from "components/atoms/image/thumbnail/ProtectedThumbnail";
import AccountId from "components/atoms/text/labels/AccountId";
import Spinner from "components/atoms/loader/Spinner";
import useAccountAccess from "hook/useAccountAccess";
import RBACWrapper from "components/blocks/function/RBACWrapper";

type Props = {
  title: string;
  installationData: InstallationView;
  print: () => void;
  email: () => void;
  componentRef: any;
  onClickRefresh: () => void;
  onClickComplete: () => void;
  onClickSearch: ClickSearch;
  isSendEmail: boolean;
  onSelectEmail: (user: string[]) => void;
  onChangeEmailMsg: (e: any) => void;
  setIsSendEmailModal: (flag: boolean) => void;
  onClickSendEmail: () => void;
  isValid: boolean;
  isValidEmail: boolean;
  confirmFinish: boolean;
  onConfirmCancel: () => void;
  onConfirm: () => void;
  isRefreshing: boolean;
  removedRecorder: boolean;
  selectedAccount: ProfileAccountInfo;
  isLoading: boolean;
  isCompleteLoading: boolean;
};

export default function InstallPresenter(props: Props): JSX.Element {
  const { intlString, lang } = useApiError();
  const { menuNavigation } = useMenu({});
  const { AccountAccessButton, AccountAccessModal } = useAccountAccess({
    buttonType: (
      <s.AccountName>
        <AccountId
          level={LEVEL_TYPE.EU}
          accountId={
            props.installationData.installationReport.targetAccountNumber || ""
          }
        />

        {props.installationData.installationReport.targetAccountName}
      </s.AccountName>
    ),

    useTemporary: false,
    isShowOnlyUser: true,
    toPath: "/recorders/list",
  });
  //TODO Send Email 2.0.x later
  const buttons = (
    <RBACWrapper requiredPermissions="manageReportRules">
      <s.ButtonWrapper>
        <mediaQuery.Default>
          <BigButton
            label="Print"
            buttonType="cancel"
            onClickBtn={props.print}
            iconComponent={<EXPORT_ICON />}
            data-testid="reports-view-installation-button-print"
          />
        </mediaQuery.Default>
        <BigButton
          label="Send Email"
          buttonType="cancel"
          onClickBtn={props.email}
          data-testid="reports-view-installation-button-email"
        />
      </s.ButtonWrapper>
    </RBACWrapper>
  );

  const completeButton = (
    <mediaQuery.Default>
      <RBACWrapper requiredPermissions="manageReportRules">
        <s.ButtonWrapper>
          <Spin spinning={props.isRefreshing}>
            <BigButton
              label={"Refresh"}
              onClickBtn={props.onClickRefresh}
              disabled={
                !props.installationData.installationReport.updatable ||
                props.removedRecorder ||
                props.isRefreshing
              }
              data-testid="reports-view-installation-button-click-refresh"
            />
          </Spin>
          <BigButton
            label={"Complete"}
            onClickBtn={props.onClickComplete}
            disabled={
              !props.installationData.installationReport.updatable ||
              props.removedRecorder
            }
            data-testid="reports-view-installation-button-click-complete"
          />
        </s.ButtonWrapper>
      </RBACWrapper>
    </mediaQuery.Default>
  );

  const titleComponent = [
    {
      label: "Report List",
      onClick: () => menuNavigation({ to: "/reports" }),
    },
    {
      // 추후 변경 필요 recorder name 으로
      label: props.installationData.title,
    },
  ];

  const isMobile = useMediaQuery({ maxWidth: mediaQuery.mobileMax });

  const itemFontSize = isMobile ? 16 : 12;
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const topInfo = [
    {
      label: "Recorder",
      value: props.installationData.recorderInfo.name,
    },
    {
      label: "Model",
      value: props.installationData.recorderInfo.model ?? "N/A",
    },
    {
      label: "Installation Date",
      value: props.installationData.installationReport.installationDate,
    },
    {
      label: "Location",
      value: props.installationData.recorderInfo.location,
    },
  ];

  const cpTopInfo = [
    {
      label: "Organization",
      value: (
        <AccountAccessButton
          accountId={props.installationData.installationReport.targetAccountId}
        />
      ),
    },
    ...topInfo,
  ];

  const customerInfo = [
    {
      label: "Name",
      value: props.installationData.installationReport.customerName,
    },
    {
      label: "Phone",
      value: props.installationData.installationReport.customerPhoneNumber,
    },
    {
      label: "Address",
      value: props.installationData.installationReport.customerLocation,
    },
  ];

  const installerInfo = [
    {
      label: "Name",
      value: props.installationData.installationReport.installerName,
    },
    {
      label: "Phone",
      value: props.installationData.installationReport.installerPhoneNumber,
    },
    {
      label: "Company",
      value: props.installationData.installationReport.installerCompany,
    },
    {
      label: "Phone",
      value:
        props.installationData.installationReport.installerCompanyPhoneNumber,
    },
    {
      label: "Address",
      value: props.installationData.installationReport.installerCompanyAddress,
    },
  ];

  return (
    <TableTemplate
      buttons={buttons}
      titleComponent={titleComponent}
      onClickSearch={props.onClickSearch}
      requiredPermissions="manageReportRules"
    >
      {props.isLoading && <Spinner isCenter />}
      <s.Container ref={props.componentRef}>
        <DetailHeader
          infos={
            selectedAccount.accountLevel === LEVEL_TYPE.EU ? topInfo : cpTopInfo
          }
          buttons={
            selectedAccount.accountLevel === LEVEL_TYPE.EU
              ? undefined
              : completeButton
          }
        />

        <s.Grid>
          {/* {props.isRefreshing && (
              <s.SpinnerContainer>
                <Spinner  size={30} aria-label="Loading Spinner"/>
              </s.SpinnerContainer>
             )} */}
          <s.LeftAside>
            {props.installationData?.cameraInfo !== undefined &&
              props.installationData?.cameraInfo.map((item, index) => {
                return (
                  <s.CameraItem key={`install-data-${index}`}>
                    <DefaultThumbnail>
                      {!item.covert ? (
                        <VideoThumbnail
                          videoLoss={false}
                          thumbnailUrl={item.thumbnailUrl}
                          errorType={item.installationStatus}
                          installationStatus={item.installationStatus}
                          isRefreshing={props.isRefreshing}
                        />
                      ) : (
                        <CovertThumbnail />
                      )}

                      {/* <img
                      src={item.url}
                      alt="camera_thumbnail"
                      /> */}
                    </DefaultThumbnail>
                    <s.CameraContent>
                      <s.CameraContentRow>
                        <Text
                          className="title"
                          fontSize={itemFontSize}
                          bold
                          color="#818CA8"
                        >
                          Camera
                        </Text>
                        <Text className="content" fontSize={itemFontSize}>
                          {item.name}
                        </Text>
                      </s.CameraContentRow>
                      <s.CameraContentRow>
                        <Text
                          className="title"
                          fontSize={itemFontSize}
                          bold
                          color="#818CA8"
                        >
                          Model
                        </Text>
                        <Text className="content" fontSize={itemFontSize}>
                          {item.model}
                        </Text>
                      </s.CameraContentRow>
                      {/* <s.CameraContentRow>
                      <Text
                      className="title"
                      fontSize={itemFontSize}
                      bold
                      color="#818CA8"
                      >
                      Web
                      </Text>
                      <Text
                      className="ip-address content"
                      fontSize={itemFontSize}
                      >
                      <Paragraph copyable>{getDomainUrl(item.url)}</Paragraph>
                      </Text> */}
                      {/*TODO pathfinder 기능완료 후 연결
                      <s.Copy className="content">
                      <div className="ip-address">
                      <span>{item.url}</span>
                      </div>
                      <button>
                      <img
                        className="copy"
                        src={COPY_ICON}
                        alt="copy icon"
                        onClick={(e) => {
                          if (item.url !== undefined) {
                            openInNewTab(item.url);
                          }
                        }}
                      />
                      </button>
                      </s.Copy> */}
                      {/* </s.CameraContentRow> */}
                    </s.CameraContent>
                  </s.CameraItem>
                );
              })}
          </s.LeftAside>
          <s.RightAside>
            {/* 추후 mapping 으로 변경필요 */}
            <s.RightWrapper>
              <s.RightWrapperTitle>
                <b>Customer</b>
              </s.RightWrapperTitle>
              <s.RightItemWrapper>
                {customerInfo.map((item, index) => {
                  if (item.value) {
                    return (
                      <s.RightItem key={`customer-info-${index}`}>
                        <h4 className="title">{item.label}</h4>
                        <p
                          className={`content ${
                            item.label === "Address" ? "address" : ""
                          }`}
                        >
                          {item.value}
                        </p>
                      </s.RightItem>
                    );
                  }
                  return null;
                })}
              </s.RightItemWrapper>
            </s.RightWrapper>

            <s.RightWrapper>
              <s.RightWrapperTitle>
                <b>Installer</b>
              </s.RightWrapperTitle>
              <s.RightItemWrapper>
                {installerInfo.map((item, index) => {
                  if (item.value) {
                    return (
                      <s.RightItem key={`installer-info-${index}`}>
                        <h4 className="title">{item.label}</h4>
                        <p
                          className={`content ${
                            item.label === "Address" ? "address" : ""
                          }`}
                        >
                          {item.value}
                        </p>
                      </s.RightItem>
                    );
                  }
                  return null;
                })}
              </s.RightItemWrapper>
            </s.RightWrapper>
          </s.RightAside>
        </s.Grid>
      </s.Container>
      {
        // 조건 추가 assigned &&
        props.isSendEmail && (
          <SmallPopupTemplate
            cancel
            title="Send Report Email"
            subTitle="Email this report to one or more participants"
            onClick={() => props.onClickSendEmail()}
            onModal={() => props.setIsSendEmailModal(false)}
            confirmLabel="Send"
            className="full-width"
            data-testid="reports-view-installation-small-popup-template-click-send-email"
          >
            <ModalInput label="Email" essential>
              <EmailSelectComponent
                //placeholder="Select User or Enter Email Address"
                onChange={props.onSelectEmail}
                placeholder={intlString(
                  lang.label.report.send.email.placeholder
                )}
                // isSelectedArr={state.user}
              />
            </ModalInput>
            <ModalInput label="Message">
              <TextArea
                placeholder="Enter your message here"
                onChange={props.onChangeEmailMsg}
                data-testid="reports-view-installation-textarea-change-email-msg"
              />
            </ModalInput>
            {!props.isValid && (
              <ValidateWord data-testid="reports-view-installation-validate-email">
                {intlString(lang.validateMsg.videoShare.err.emptyUser)}
              </ValidateWord>
            )}

            {!props.isValidEmail && (
              <ValidateWord data-testid="reports-view-installation-validate-email-invalid">
                {intlString(lang.validateMsg.videoShare.err.invalidEmail)}
              </ValidateWord>
            )}
          </SmallPopupTemplate>
        )
      }

      {props.confirmFinish && (
        <SmallPopupDefaultTemplate
          onModal={props.onConfirmCancel}
          className="install-confirm-modal"
          button={
            <>
              <TableButton
                label="Cancel"
                onClick={props.onConfirmCancel}
                buttonType="cancel"
                data-testid="reports-view-installation-table-button-confirm-cancel"
              />

              <TableButton
                label="Complete"
                onClick={props.onConfirm}
                buttonType="primary"
                disabled={props.isCompleteLoading}
                data-testid="reports-view-installation-table-button-confirm"
              />
            </>
          }
        >
          {props.isCompleteLoading && <Spinner isCenter />}
          <s.ConfirmModalTitle>
            <FaTools /> Complete Installation Report?
          </s.ConfirmModalTitle>
          <s.ConfirmModalContent>
            This report will be shared with the end-user "
            {props.installationData.installationReport.targetAccountName}"
            organization.
            <br />
            Please note that you cannot make changes after completion.
          </s.ConfirmModalContent>
        </SmallPopupDefaultTemplate>
      )}
      {AccountAccessModal}
    </TableTemplate>
  );
}
